import { curveBasis } from "d3-shape";
import React from "react";
import { defineMessage, useIntl } from "react-intl";
import { LineChart } from "../charts/LineChart";
import { withFigureIO } from "../components/figure";
import { useReadScrolly } from "../components/scrolly";
import { metadata } from "../data/data_equity_fig_GPITREND";
import { datasets } from "../data/data_equity_fig_GPITREND_datasets";
import { getLevelName, getRegionCategoryName, mkGemEntityDecoder, ordLevel, useButtonGroupState, useFigureControlItems, useNamedEntitiesWithLevel, useRegionEntities } from "../domain";
import { useMedia } from "../hooks";
import * as M from "../materials";
import { breakpoints } from "../materials/consts.stylex";
import { O, pipe } from "../prelude";
export * from "../data/data_equity_fig_GPITREND";
const DEFAULT_REGION_TYPE = "income";
export const Data = mkGemEntityDecoder([
    "ind_id",
    "level",
    "year",
    "value"
], {});
export const Chart = ({ data })=>{
    const intl = useIntl();
    const screenSDown = !useMedia(breakpoints.s);
    const screenMDown = !useMedia(breakpoints.m);
    const [regionType, actions] = useButtonGroupState("regionType");
    const { activeSection } = useReadScrolly();
    const [scrollyRegionType, scrollyId] = React.useMemo(()=>pipe(activeSection, // Format: "income,1", but we throw away the "1" (sections need to have distinct IDs)
        O.map((str)=>{
            const [a = "", b = ""] = str.split(",");
            return [
                a,
                b
            ];
        }), O.getOrElse(()=>[
                DEFAULT_REGION_TYPE,
                "0"
            ])), [
        activeSection
    ]);
    const entityData = useNamedEntitiesWithLevel(data);
    const regionData = useRegionEntities(entityData);
    const levels = useFigureControlItems(entityData, (x)=>x.level, (x)=>getLevelName(intl, x.level), {
        ord: ordLevel
    });
    React.useEffect(()=>{
        actions.setSelectionControl("regionType", {
            type: "ButtonGroup",
            selected: DEFAULT_REGION_TYPE
        }, [
            {
                value: "income",
                label: getRegionCategoryName(intl, "income")
            },
            {
                value: "geo",
                label: getRegionCategoryName(intl, "geo")
            }
        ]);
    }, [
        intl,
        actions
    ]);
    React.useEffect(()=>{
        if (O.isSome(activeSection)) {
            actions.updateSelectionControl("regionType", scrollyRegionType);
        }
    }, [
        actions,
        activeSection,
        scrollyRegionType,
        scrollyId
    ]);
    const chartData = React.useMemo(()=>{
        return regionData.filter((x)=>x.entity_type === regionType);
    }, [
        regionData,
        regionType
    ]);
    return <LineChart tLabel={(s)=>s} height={screenSDown ? M.chartHeight.xs : screenMDown ? M.chartHeight.s : M.chartHeight.m} minInnerWidth={screenSDown ? 60 : undefined} x="year" yTicks={[
        0.4,
        0.6,
        0.8,
        1,
        1.2,
        1.4
    ]} yAnnotations={[
        {
            type: "band",
            label: intl.formatMessage(defineMessage({
                id: "Parity",
                defaultMessage: "Parity"
            })),
            range: [
                0.95,
                1.05
            ],
            dy: screenMDown ? "-0.7em" : undefined
        },
        {
            type: "line",
            value: 1
        }
    ]} zero={false} yNice={0} timeParse="%Y" timeFormat="%Y" numberFormat=".2f" column="level_name" columns={levels.length} columnSort="none" category="datum.entity_name" colorLegend endLabel={false} values={chartData} showTooltip curve={curveBasis}/>;
};
export default withFigureIO({
    url: new URL("../data/data_equity_fig_GPITREND.json", import.meta.url).href,
    csv: new URL("../data/data_equity_fig_GPITREND.zip", import.meta.url).href,
    xlsx: new URL("../data/data_equity_fig_GPITREND.xlsx", import.meta.url).href,
    metadata,
    Data,
    Chart,
    size: "wide",
    datasets
});
